import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { loadNavigation } from "~/features/navigation/redux/navigationThunks";
import type { AppDispatch } from "~/app/store";
import { clearOccToken } from "~/app/api/occ/occToken";
import type { ComponentProps } from "~/componentProps";
import { createNavigationBreadcrumbs } from "~/bridges/createNavigationBreadcrumbs";
import { createHeaderNavigation } from "~/bridges/header/HeaderNavigationComponent";
import { createRegionSelectionBanner } from "~/bridges/createRegionSelectionBanner";
import { createBrowserNotSupportedPopup } from "~/bridges/createBrowserNotSupportedPopup";
import { createPageFooter } from "~/common/components/PageFooter/PageFooter";
import { createBottomNavigation } from "~/features/BottomNavigation/BottomNavigation";
import { createAffiliateSalesPartnerBanner } from "~/features/affiliate/components/AffiliateSalesPartnerBanner/AffiliateSalesPartnerBanner";
import { createAffiliateSalesPartnerPopup } from "~/features/affiliate/components/AffiliateSalesPartnerPopup/AffiliateSalesPartnerPopup";

/**
 * Shell-frame and CMS-crossing components the storefront shell renders by
 * name. This local, typed map replaces the retired `window.SCHA.cmps` global
 * registry seam (task 0065 phase 1 / island-taxonomy buckets: 6 shell-frame +
 * Navigation + Breadcrumbs + FooterNavigation). All entries are boot-needed
 * on every classic page, so they stay static entry imports by design.
 *
 * Tests stub these through `vi.mock("~/app/shell/shellRegisteredComponents")`
 * instead of seeding the old global registry.
 */

const NavigationMount = (props: ComponentProps["Navigation"]) => {
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    if (props) {
      dispatch(loadNavigation(props));
    }
  }, [dispatch, props]);

  return null;
};

const BreadcrumbsMount = (props: ComponentProps["Breadcrumbs"]) => createNavigationBreadcrumbs(props);
const HeaderNavigationMount = (props: ComponentProps["HeaderNavigation"]) =>
  createHeaderNavigation(props, clearOccToken);
const FooterNavigationMount = (props: ComponentProps["FooterNavigation"]) => createPageFooter(props);
const RegionSelectionBannerMount = () => createRegionSelectionBanner();
const AffiliateSalesPartnerBannerMount = () => createAffiliateSalesPartnerBanner();
const AffiliateSalesPartnerPopupMount = () => createAffiliateSalesPartnerPopup();
const BrowserNotSupportedPopupMount = () => createBrowserNotSupportedPopup();
const BottomNavigationComponentMount = () => createBottomNavigation();

export const SHELL_REGISTERED_COMPONENTS = {
  Navigation: NavigationMount,
  Breadcrumbs: BreadcrumbsMount,
  HeaderNavigation: HeaderNavigationMount,
  FooterNavigation: FooterNavigationMount,
  RegionSelectionBanner: RegionSelectionBannerMount,
  AffiliateSalesPartnerBanner: AffiliateSalesPartnerBannerMount,
  AffiliateSalesPartnerPopup: AffiliateSalesPartnerPopupMount,
  BrowserNotSupportedPopup: BrowserNotSupportedPopupMount,
  BottomNavigationComponent: BottomNavigationComponentMount,
} as const;

export type ShellComponentName = keyof typeof SHELL_REGISTERED_COMPONENTS;
